/**
 * EINPanel — IRS EIN management for a company.
 *
 * Shows current EIN (formatted XX-XXXXXXX), verification status,
 * verification score breakdown, and admin controls to update + mark
 * verified. Score logic mirrors backend: 25 (provided) + 25 (valid
 * format) + 50 (verified by admin).
 *
 * Used on FounderDashboardPage today; reusable on a future
 * CompanyDetailPage.
 */
import React, { useEffect, useState } from 'react';
import {
  ShieldCheck, Loader2, ExternalLink, Pencil, Check, AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

interface EinSnapshot {
  company_id: number;
  company_name: string | null;
  ein: string | null;
  formatted: string | null;
  verified: boolean;
  verified_at: string | null;
  notes: string | null;
  campus: string | null;
  score: number;
  signals: string[];
  concerns: string[];
  likely_tax_exempt: boolean;
}

interface Props {
  companyId: number;
  /** Show the "Mark verified" admin control. Founders shouldn't see it. */
  isAdmin?: boolean;
}

const tierColor = (s: number): string => {
  if (s >= 100) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (s >= 50)  return 'bg-amber-50 text-amber-700 border-amber-200';
  if (s > 0)    return 'bg-orange-50 text-orange-700 border-orange-200';
  return 'bg-gray-100 text-gray-600 border-gray-200';
};


const EINPanel: React.FC<Props> = ({ companyId, isAdmin = false }) => {
  const [snap, setSnap] = useState<EinSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [einInput, setEinInput] = useState('');
  const [notesInput, setNotesInput] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<EinSnapshot>(`/companies/${companyId}/ein`);
      setSnap(data);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load EIN');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [companyId]);

  const beginEdit = () => {
    setEinInput(snap?.formatted || '');
    setNotesInput(snap?.notes || '');
    setEditing(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/companies/${companyId}/ein`, {
        ein: einInput.trim(),
        notes: notesInput.trim() || null,
      });
      toast.success('EIN saved');
      setEditing(false);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to save EIN');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkVerified = async () => {
    if (!snap?.ein) {
      toast.error('Set an EIN first');
      return;
    }
    if (!window.confirm(
      `Confirm the IRS TEOS lookup matched ${snap.company_name || 'this company'}?`
    )) return;
    try {
      await api.post(`/companies/${companyId}/ein/mark-verified`);
      toast.success('EIN marked verified');
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to mark verified');
    }
  };

  if (loading) {
    return (
      <section className="bg-white border border-gray-200 rounded-lg p-5 flex items-center gap-2 text-gray-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading EIN…
      </section>
    );
  }

  if (!snap) return null;

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-indigo-500" />
            IRS EIN — entity validation
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            The IRS has no live API; verification is operator-driven via the IRS TEOS lookup.
          </p>
        </div>
        <div className={`px-2 py-1 text-xs font-semibold border rounded-md ${tierColor(snap.score)}`}>
          {snap.score}/100
        </div>
      </div>

      {!editing ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">EIN</div>
              <div className="font-mono text-base">
                {snap.formatted || <span className="text-gray-400">— not set —</span>}
              </div>
              {snap.campus && snap.campus !== 'Unknown' && (
                <div className="text-[10px] text-gray-500 mt-0.5">
                  Issued by IRS {snap.campus} campus
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {snap.verified && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                  <Check className="w-3.5 h-3.5" /> Verified
                  {snap.verified_at && (
                    <span className="text-gray-400">
                      ({new Date(snap.verified_at).toLocaleDateString()})
                    </span>
                  )}
                </span>
              )}
              <button
                onClick={beginEdit}
                className="text-xs px-2 py-1 rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 inline-flex items-center gap-1"
              >
                <Pencil className="w-3 h-3" /> Edit
              </button>
            </div>
          </div>

          {/* Signals + concerns */}
          {snap.signals.length > 0 && (
            <div className="text-xs space-y-0.5">
              {snap.signals.map((s, i) => (
                <div key={i} className="text-emerald-700 flex items-start gap-1">
                  <Check className="w-3 h-3 mt-0.5 flex-shrink-0" /> <span>{s}</span>
                </div>
              ))}
            </div>
          )}
          {snap.concerns.length > 0 && (
            <div className="text-xs space-y-0.5">
              {snap.concerns.map((c, i) => (
                <div key={i} className="text-amber-700 flex items-start gap-1">
                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> <span>{c}</span>
                </div>
              ))}
            </div>
          )}

          {snap.notes && (
            <div className="text-xs bg-gray-50 rounded p-2 text-gray-700">
              <span className="font-medium text-gray-500">Notes:</span> {snap.notes}
            </div>
          )}

          {/* Action row */}
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            {snap.likely_tax_exempt ? (
              <a
                href={`https://apps.irs.gov/app/eos/allSearch?searchTextField=${encodeURIComponent(snap.company_name || '')}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" /> Look up on IRS TEOS (likely tax-exempt)
              </a>
            ) : (
              <a
                href="https://www.irs.gov/businesses/small-businesses-self-employed/employer-id-numbers"
                target="_blank"
                rel="noreferrer"
                className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" /> IRS EIN info
              </a>
            )}
            {isAdmin && snap.ein && !snap.verified && (
              <button
                onClick={handleMarkVerified}
                className="ml-auto inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium"
              >
                <Check className="w-3 h-3" /> Mark verified
              </button>
            )}
          </div>
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              EIN (format: XX-XXXXXXX)
            </label>
            <input
              type="text"
              value={einInput}
              onChange={(e) => setEinInput(e.target.value)}
              placeholder="13-3456789"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notesInput}
              onChange={(e) => setNotesInput(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Verified via IRS TEOS on…"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-xs px-3 py-1.5 rounded text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-gray-300"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />} Save
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default EINPanel;
